import { siteLevels, buildingID } from './siteBuildings.js';
import { ptInPoly, distToSeg, polyArea } from './geometry.js';

export const SITE_GROUND = '@site-ground';
export const isGroundLevel = level => Math.abs(level.elevation || 0) < 0.001;
export const groundLevels = S => siteLevels(S).filter(isGroundLevel);
export const groundFloor = (S, owner = buildingID(S)) => groundLevels(S).find(f => f.buildingId === owner);
export const levelChoices = S => [
  ...(groundLevels(S).length ? [[(groundFloor(S) || groundLevels(S)[0]).id, 'Site ground']] : []),
  ...siteLevels(S).filter(f => f.buildingId === buildingID(S) && !isGroundLevel(f))
    .sort((a, b) => a.elevation - b.elevation).map(f => [f.id, f.name])
];

const collections = ['areas', 'walls', 'openings', 'zones', 'cams', 'models', 'equipment', 'cables', 'accessories', 'targets', 'lights', 'tasks', 'obstacles'];

/** A disposable rendering projection, never an editable/saved document. Floor
 * ownership remains in the original snapshots. Qualify foreign IDs because
 * imported floors can legally reuse object and model IDs. */
export function siteGroundPlan(S) {
  const levels = groundLevels(S);
  if (!isGroundLevel(S) || levels.length < 2) return S;
  const ids = new Set(collections.flatMap(key => siteLevels(S).flatMap(f => (f.plan[key] || []).map(o => o.id))));
  const selections = {};
  const plans = levels.map(f => {
    if (f.id === S.activeFloorId) return S;
    const mapping = new Map();
    for (const key of collections) for (const o of f.plan[key] || []) {
      if (mapping.has(o.id)) continue;
      let id = JSON.stringify(['site-ground', f.id, o.id]);
      while (ids.has(id)) id += '~';
      ids.add(id); mapping.set(o.id, id); selections[id] = { id: o.id, floorId: f.id };
    }
    const remap = (value, key = '') => {
      if (Array.isArray(value)) return value.map(v => remap(v, key));
      if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, remap(v, k)]));
      return typeof value === 'string' && (key === 'id' || /Ids?$/.test(key)) && mapping.has(value) ? mapping.get(value) : value;
    };
    return { sceneState: f.plan.sceneState, ...Object.fromEntries(collections.map(key => [key, remap(f.plan[key] || [])])) };
  });
  return { ...S, ...Object.fromEntries(collections.map(key => [key, plans.flatMap(p => p[key] || [])])),
    // Occupancy belongs to the source plan. Selecting a new building must not
    // reinterpret an occupied car as hidden in that building's design state.
    obstacles: plans.flatMap(p => (p.obstacles || []).map(o => ({ ...o, _groundSceneState: p.sceneState || 'design' }))),
    // Keep physical levels available to visibility/slabs without representing
    // the combined ground as an additional physical storey.
    _siteLevels: siteLevels(S), _groundSelections: selections, _groundFloorIds: levels.map(f => f.id),
    _stairLevels: siteLevels(S).map(({ plan, ...f }) => f) };
}

export function groundSelection(plan, selection) {
  if (!selection) return null;
  if (selection.type === 'stair' && plan._groundFloorIds) {
    const stair = plan.stairs.find(s => s.id === selection.id);
    const floorId = plan._groundFloorIds.find(id => stair && [stair.fromFloorId, stair.toFloorId].includes(id));
    if (floorId) return { ...selection, floorId };
  }
  const source = plan._groundSelections?.[selection.id];
  return source ? { ...selection, ...source } : selection;
}

/** Route a new object by an actual indoor polygon, then by outdoor ground.
 * Empty land keeps the explicitly selected destination building. */
export function groundOwnerAt(S, { x, y }) {
  if (!isGroundLevel(S)) return null;
  const candidates = groundLevels(S).flatMap(f => (f.plan.areas || []).filter(a => a.kind === 'floor' && (
    ptInPoly(x, y, a.pts) || a.pts.some((p, i) => { const q = a.pts[(i + 1) % a.pts.length]; return distToSeg(x, y, p.x, p.y, q.x, q.y) < .001; })
  )).map(a => ({ floorId: f.id, outdoor: a.environment === 'outdoor', area: Math.abs(polyArea(a.pts)) })));
  return candidates.sort((a, b) => Number(a.outdoor) - Number(b.outdoor) || a.area - b.area)[0]?.floorId || null;
}
